import { isAfter } from '@shared/helpers/datetime/datetime';

type Comparison<T> = (item1: T, item2: T) => number;

export function compare<T>(...comparisons: Array<Comparison<T>>): Comparison<T> {
    let result: Comparison<T> = () => 0;

    for (const comparison of comparisons) {
        result = combineComparisons(result, comparison);
    }

    return result;
}

export function numberComparer(item1: number, item2: number) {
    if (item1 === item2) {
        return 0;
    } else {
        return item1 < item2 ? -1 : 1;
    }
}

export function onNumber<T>(projection: (item: T) => number): Comparison<T> {
    return on(projection, numberComparer);
}

function localeStringComparer(item1: string, item2: string) {
    return item1.localeCompare(item2);
}

export function onLocaleString<T>(projection: (item: T) => string): Comparison<T> {
    return on(projection, localeStringComparer);
}

function booleanComparer(item1: boolean, item2: boolean) {
    if (item1 === item2) {
        return 0;
    } else {
        return item1 ? 1 : -1;
    }
}

export function onBoolean<T>(selector: (item: T) => boolean): Comparison<T> {
    return on(selector, booleanComparer);
}

function dateComparer(item1: Date, item2: Date) {
    if (isAfter(item1, item2)) {
        return 1;
    } else {
        return -1;
    }
}

export function onDate<T>(projection: (item: T) => Date): Comparison<T> {
    return on(projection, dateComparer);
}

export function on<TItem, TProperty>(
    selector: (item: TItem) => TProperty,
    comparer: Comparison<TProperty>
): Comparison<TItem> {
    return (x, y) => comparer(selector(x), selector(y));
}

export function reversed<T>(comparison: Comparison<T>): Comparison<T> {
    return (x, y) => comparison(y, x);
}

function combineComparisons<T>(first: Comparison<T>, second: Comparison<T>): Comparison<T> {
    return (x, y) => {
        const resultOfFirst = first(x, y);

        if (resultOfFirst !== 0) {
            return resultOfFirst;
        }

        return second(x, y);
    };
}
